import { ReactNode, useState, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LucideIcon, LogOut, ArrowLeftRight, MoreHorizontal, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FeedbackButton } from '@/components/shared/feedback-button';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ArchonIcon } from '@/components/icons/archon-icon';
import { NotificationBell } from '@/components/notification-bell';
import { ThemeToggle } from '@/components/theme-toggle';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageMetaProvider, usePageMetaContext } from '@/hooks/use-page-meta';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';

// Theme configurations for each role
// Coach = hard court blue, Director = Wimbledon grass green, Parent = clay terracotta
export const roleThemes = {
  coach: {
    gradient: 'from-sky-500 to-blue-600',
    activeTextColor: 'text-sky-700',
    activeMobileTextColor: 'text-sky-700 dark:text-sky-400',
    activeBorderColor: 'border-sky-600',
  },
  director: {
    gradient: 'from-emerald-500 to-green-700',
    activeTextColor: 'text-emerald-700',
    activeMobileTextColor: 'text-emerald-700 dark:text-emerald-400',
    activeBorderColor: 'border-emerald-600',
  },
  parent: {
    gradient: 'from-orange-400 to-orange-600',
    activeTextColor: 'text-orange-700',
    activeMobileTextColor: 'text-orange-700 dark:text-orange-400',
    activeBorderColor: 'border-orange-500',
  },
} as const;

/** IDs of nav items to show in the mobile bottom bar (max 4). Rest appear in "More" sheet. */
export type BottomNavIds = string[];

export type RoleTheme = keyof typeof roleThemes;

export interface NavItem {
  id: string;
  href: string;
  icon: LucideIcon;
  label: string;
  comingSoon?: boolean;
}

/** Optional sidebar grouping for desktop nav (Director has 12 items). */
export interface SidebarGroup {
  label: string;
  ids: string[];
}

interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within an AppSidebar');
  }
  return context;
}

interface AppSidebarProps {
  children: ReactNode;
  navItems: NavItem[];
  /** IDs to pin in the mobile bottom bar (max 4). Defaults to first 4 items. */
  bottomNavIds?: string[];
  /** Optional grouping for desktop sidebar nav. */
  sidebarGroups?: SidebarGroup[];
  role: RoleTheme;
  portalName: string;
  user: {
    email?: string;
  } | null;
  onLogout: () => void;
  switchPortal?: { label: string; href: string };
}

function NavItemButton({
  item,
  isActive,
  isCollapsed,
  isMobile,
  theme,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
  isMobile: boolean;
  theme: typeof roleThemes[RoleTheme];
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={item.comingSoon}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
        !isCollapsed || isMobile ? 'justify-start' : 'justify-center',
        item.comingSoon
          ? 'text-white/30 cursor-not-allowed'
          : isActive
            ? "bg-primary/10 text-primary font-nunito font-bold"
            : 'text-white/70 hover:bg-white/5 hover:text-white'
      )}
    >
      <item.icon className="w-5 h-5 flex-shrink-0 shrink-0" />
      {(!isCollapsed || isMobile) && (
        <>
          <span className="flex-1 text-left">{item.label}</span>
          {item.comingSoon && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-white/10 text-white/50 rounded">
              Soon
            </span>
          )}
        </>
      )}
    </button>
  );
}

function SidebarNav({
  navItems,
  sidebarGroups,
  activeTab,
  isCollapsed,
  isMobile,
  theme,
  onNavClick,
}: {
  navItems: NavItem[];
  sidebarGroups?: SidebarGroup[];
  activeTab: string;
  isCollapsed: boolean;
  isMobile: boolean;
  theme: typeof roleThemes[RoleTheme];
  onNavClick: (item: NavItem) => void;
}) {
  if (sidebarGroups && (!isCollapsed || isMobile)) {
    return (
      <nav className="space-y-4">
        {sidebarGroups.map((group) => {
          const items = group.ids
            .map((id) => navItems.find((i) => i.id === id))
            .filter(Boolean) as NavItem[];
          return (
            <div key={group.label}>
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-white/35">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavItemButton
                    key={item.id}
                    item={item}
                    isActive={activeTab === item.id}
                    isCollapsed={isCollapsed}
                    isMobile={isMobile}
                    theme={theme}
                    onClick={() => onNavClick(item)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    );
  }

  // Flat list (collapsed or no groups)
  return (
    <nav className="space-y-0.5">
      {navItems.map((item) => {
        const button = (
          <NavItemButton
            key={item.id}
            item={item}
            isActive={activeTab === item.id}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            theme={theme}
            onClick={() => onNavClick(item)}
          />
        );
        if (isCollapsed && !isMobile) {
          return (
            <Tooltip key={item.id} delayDuration={0}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent side="right" className="flex items-center gap-2">
                {item.label}
                {item.comingSoon && <span className="text-[10px] opacity-70">Coming Soon</span>}
              </TooltipContent>
            </Tooltip>
          );
        }
        return button;
      })}
    </nav>
  );
}

function DesktopHeader({
  activeLabel,
  role,
}: {
  activeLabel: string;
  role: RoleTheme;
}) {
  const { meta } = usePageMetaContext();

  return (
    <div className="flex items-center gap-1">
      <ThemeToggle className="hover:bg-accent text-muted-foreground hover:text-foreground" />
      <NotificationBell role={role} className="text-muted-foreground hover:bg-accent hover:text-foreground" />
    </div>
  );
}

export function AppSidebar({
  children,
  navItems,
  bottomNavIds,
  sidebarGroups,
  role,
  portalName,
  user,
  onLogout,
  switchPortal,
}: AppSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = roleThemes[role];

  const baseHref = `/${role}`;
  const activeTab =
    navItems.find(
      (item) =>
        item.href === pathname ||
        (item.href !== baseHref && pathname.startsWith(item.href))
    )?.id || 'dashboard';

  // Split items for mobile bottom nav
  const pinnedIds = bottomNavIds ?? navItems.slice(0, 4).map((i) => i.id);
  const bottomItems = navItems.filter((i) => pinnedIds.includes(i.id));
  const moreItems = navItems.filter((i) => !pinnedIds.includes(i.id));

  const handleNavClick = (item: NavItem) => {
    if (!item.comingSoon) {
      navigate(item.href);
      setIsMoreOpen(false);
    }
  };

  const SidebarNavContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo Section */}
      <div
        className={cn(
          'flex items-center gap-3 px-4 h-16 shrink-0',
          !isMobile && isCollapsed && 'justify-center px-2'
        )}
      >
        <ArchonIcon className="w-10 h-10 shrink-0" />
        {(!isCollapsed || isMobile) && (
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-lg text-white leading-tight">Tennis Coach</span>
            <span className="text-xs text-white/60">{portalName}</span>
          </div>
        )}
      </div>

      <div className="mx-3 h-px bg-white/10" />

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <SidebarNav
          navItems={navItems}
          sidebarGroups={sidebarGroups}
          activeTab={activeTab}
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          theme={theme}
          onNavClick={handleNavClick}
        />
      </ScrollArea>

      <div className="mx-3 h-px bg-white/10" />

      {/* Switch Portal */}
      {switchPortal && (
        <div className="px-3 py-2">
          {isCollapsed && !isMobile ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate(switchPortal.href)}
                  className="w-full flex justify-center px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
                >
                  <ArrowLeftRight className="w-5 h-5 flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{switchPortal.label}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={() => navigate(switchPortal.href)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all text-sm font-medium"
            >
              <ArrowLeftRight className="w-5 h-5 flex-shrink-0" />
              <span>{switchPortal.label}</span>
            </button>
          )}
        </div>
      )}

      {/* Collapse toggle — desktop only, inside sidebar */}
      {!isMobile && (
        <div className="px-3 py-1">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/50 hover:bg-white/10 hover:text-white transition-all text-sm',
                  isCollapsed && 'justify-center'
                )}
              >
                {isCollapsed
                  ? <PanelLeftOpen className="w-4 h-4 flex-shrink-0" />
                  : <PanelLeftClose className="w-4 h-4 flex-shrink-0" />
                }
                {!isCollapsed && <span>Collapse</span>}
              </button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side="right">Expand sidebar</TooltipContent>
            )}
          </Tooltip>
        </div>
      )}

      {/* Feedback */}
      <div className="px-3 py-1">
        <FeedbackButton isCollapsed={isCollapsed && !isMobile} />
      </div>

      <div className="mx-3 h-px bg-white/10" />

      {/* User Section */}
      <div className={cn('p-3', !isCollapsed || isMobile ? '' : 'flex flex-col items-center')}>
        {(!isCollapsed || isMobile) ? (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white/10">
            <div
              className={cn(
                'w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br text-white font-semibold text-sm shrink-0',
                theme.gradient
              )}
            >
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {user?.email?.split('@')[0] || role}
              </div>
              <div className="text-xs text-white/60 capitalize">{role}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="flex-shrink-0 text-white/60 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                className="w-10 h-10 text-white/60 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Sign out</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );

  return (
    <PageMetaProvider>
      <TooltipProvider>
        <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed }}>
          <div className="min-h-screen bg-background">
            {/* Desktop Sidebar */}
            <aside
              className={cn(
                'fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-white/10 transition-all duration-300 hidden lg:flex flex-col',
                isCollapsed ? 'w-[72px]' : 'w-64'
              )}
            >
              <SidebarNavContent />
            </aside>

            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 h-14 bg-sidebar flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <ArchonIcon className="w-7 h-7 shrink-0" />
                <MobileTopBarTitle />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <ThemeToggle className="hover:bg-white/20 text-white" />
                <FeedbackButton isCollapsed={true} />
                <NotificationBell role={role} />
              </div>
            </header>

            {/* Desktop Header Bar */}
            <DesktopHeaderBar
              isCollapsed={isCollapsed}
              role={role}
            />

            {/* Main Content */}
            <main
              className={cn(
                'transition-all duration-300',
                isCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64',
                'pb-16 lg:pb-0',
                'min-h-[calc(100vh-3.5rem)] lg:min-h-[calc(100vh-4rem)]'
              )}
            >
              <div className="max-w-7xl mx-auto p-4 lg:p-6">{children}</div>
            </main>

            {/* Mobile Bottom Nav */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border flex items-stretch h-16">
              {bottomItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item)}
                    disabled={item.comingSoon}
                    className={cn(
                      'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors min-w-0 px-1',
                      item.comingSoon
                        ? 'text-muted-foreground/40'
                        : isActive
                          ? theme.activeMobileTextColor
                          : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'stroke-[2.5]')} />
                    <span className="truncate w-full text-center">{item.label}</span>
                  </button>
                );
              })}

              {moreItems.length > 0 && (
                <button
                  onClick={() => setIsMoreOpen(true)}
                  className={cn(
                    'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors px-1',
                    isMoreOpen ? theme.activeMobileTextColor : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <MoreHorizontal className="w-5 h-5 flex-shrink-0" />
                  <span>More</span>
                </button>
              )}
            </nav>

            {/* More sheet */}
            <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
              <SheetContent side="bottom" className={cn('p-0 rounded-t-2xl flex flex-col overflow-hidden', moreItems.length > 4 ? 'h-[80vh]' : 'max-h-[80vh]')}>
                <SheetTitle className="sr-only">More options</SheetTitle>
                <div className="flex flex-col min-h-0 flex-1">
                  {/* Handle bar */}
                  <div className="flex justify-center pt-3 pb-1 shrink-0">
                    <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
                  </div>
                  {/* Sheet header with portal name */}
                  <div className="px-4 py-2 flex items-center gap-3 shrink-0">
                    <ArchonIcon className="w-8 h-8 shrink-0" />
                    <span className="font-semibold text-foreground text-sm">{portalName}</span>
                  </div>
                  <div className="h-px bg-border mx-4 shrink-0" />
                  <ScrollArea className="flex-1 px-4">
                    {moreItems.length > 0 && (
                      <div className="py-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1">Navigation</p>
                        <div className="space-y-0.5">
                          {moreItems.map((item) => {
                            const isActive = activeTab === item.id;
                            return (
                              <button
                                key={item.id}
                                onClick={() => handleNavClick(item)}
                                disabled={item.comingSoon}
                                className={cn(
                                  'w-full flex items-center gap-3 px-3 py-4 rounded-xl text-sm font-medium transition-colors',
                                  item.comingSoon
                                    ? 'text-muted-foreground/40 cursor-not-allowed'
                                    : isActive
                                      ? cn('bg-muted font-semibold', theme.activeMobileTextColor)
                                      : 'text-foreground hover:bg-muted'
                                )}
                              >
                                <item.icon className="w-5 h-5 flex-shrink-0" />
                                <span className="flex-1 text-left">{item.label}</span>
                                {item.comingSoon && <span className="text-[10px] text-muted-foreground">Soon</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </ScrollArea>
                  {switchPortal && (
                    <>
                      <div className="h-px bg-border mx-4 shrink-0" />
                      <button
                        onClick={() => { navigate(switchPortal.href); setIsMoreOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-4 rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors shrink-0"
                      >
                        <ArrowLeftRight className="w-5 h-5 flex-shrink-0" />
                        <span>{switchPortal.label}</span>
                      </button>
                    </>
                  )}
                  {/* Profile / logout — always visible at the bottom */}
                  <div className="h-px bg-border mx-4 shrink-0" />
                  <div className="flex items-center gap-3 px-3 py-3 shrink-0">
                    <div className={cn('w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br text-white font-semibold text-sm flex-shrink-0', theme.gradient)}>
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{user?.email?.split('@')[0] || role}</p>
                      <p className="text-xs text-muted-foreground capitalize">{role}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onLogout} className="text-muted-foreground hover:text-foreground gap-1.5 shrink-0">
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </Button>
                  </div>
                  <div className="h-safe pb-2 shrink-0" />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </SidebarContext.Provider>
      </TooltipProvider>
    </PageMetaProvider>
  );
}

/** Separate component so it can read PageMetaContext (which is provided inside AppSidebar). */
function DesktopHeaderBar({
  isCollapsed,
  role,
}: {
  isCollapsed: boolean;
  role: RoleTheme;
}) {
  const { meta } = usePageMetaContext();
  const orgName = useQuery(api.shared.organization.getOrgName);

  return (
    <header
      className={cn(
        'hidden lg:flex sticky top-0 z-30 h-16 bg-card/80 backdrop-blur-sm border-b border-border items-center justify-between px-6 transition-all duration-300',
        isCollapsed ? 'ml-[72px]' : 'ml-64'
      )}
    >
      <div className="flex flex-col justify-center">
        {orgName === undefined ? (
          <div className="h-5 w-40 animate-pulse rounded-md bg-primary/10" />
        ) : (
          <p className="font-nunito font-bold text-xl text-foreground leading-tight">
            {orgName ?? 'Tennis Coach'}
          </p>
        )}
        {meta.subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5">{meta.subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <ThemeToggle className="hover:bg-accent text-muted-foreground hover:text-foreground" />
        <NotificationBell role={role} className="text-muted-foreground hover:bg-accent hover:text-foreground" />
      </div>
    </header>
  );
}

/** Mobile top bar title — separate component so useQuery can be called inside AppSidebar JSX. */
function MobileTopBarTitle() {
  const orgName = useQuery(api.shared.organization.getOrgName);
  if (orgName === undefined) {
    return <div className="h-4 w-32 animate-pulse rounded-md bg-white/20" />;
  }
  return (
    <span className="font-semibold text-white text-sm truncate">
      {orgName ?? 'Tennis Coach'}
    </span>
  );
}
